import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Agenda,
  AgendaItemDoc,
  AgendaItemWithTimes,
  Event,
  Track,
} from '@livekatsomo/models';
import { UpdateAgendaOrderFunction } from '@livekatsomo/types';
import { useListMenu } from '@livekatsomo/web/hooks';
import {
  ListItemMenu,
  ListItemMenuProps,
} from '@livekatsomo/web/ui-components/layout';
import { SortableList } from '@livekatsomo/web/ui-components/sortable-list';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import List from '@mui/material/List';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import {
  AgendaAdminCardListItem,
  AgendaListItemProps,
} from '../AgendaAdminCardListItem/AgendaAdminCardListItem';
import { useAgendaItemsWithStartTime } from '../useAgendaItemsWithStartTime/useAgendaItemsWithStartTime';

/**
 * Props for the AgendaAdminCard component.
 */
export interface AgendaAdminCardProps {
  /**
   * The list of agenda items to display.
   */
  agendaItems: Agenda;
  /**
   * The track to which the agenda items belong.
   */
  track: Track;
  /**
   * The event to which the track belongs.
   */
  event: Event;
  /**
   * Callback function to handle selection of an agenda item.
   * @param agendaItem - The selected agenda item.
   */
  onSelect?: (agendaItem: AgendaItemDoc) => void;
  /**
   * Callback function to handle deletion of an agenda item.
   * @param agendaItem - The agenda item to delete.
   */
  onDelete?: ListItemMenuProps<AgendaItemDoc>['onDelete'];
  /**
   * Callback function to handle editing of an agenda item.
   * @param agendaItem - The agenda item to edit.
   */
  onEdit?: ListItemMenuProps<AgendaItemDoc>['onEdit'];
  /**
   * Callback function to handle opening of a dialog to add a new agenda item.
   * @param index - The index of the new agenda item.
   */
  onOpenAddAgendaItemDialog?: (index?: number) => void;
  /**
   * Callback function to handle updating the order of agenda items after drag-and-drop.
   * @param items - The updated list of agenda items.
   */
  onUpdateOrder?: UpdateAgendaOrderFunction;
  /**
   * Callback function to handle setting an agenda item as the current item for the track.
   * @param agendaItem - The agenda item to set as current.
   */
  onSetAsCurrent?: ListItemMenuProps<AgendaItemDoc>['onSetAsCurrent'];
}

/**
 * A card component that displays a list of agenda items for an event track in an admin view.
 * Allows for drag-and-drop reordering of agenda items, as well as editing and deleting individual items.
 */
export function AgendaAdminCard({
  agendaItems,
  track,
  event,
  onSelect,
  onDelete,
  onEdit,
  onOpenAddAgendaItemDialog,
  onUpdateOrder,
  onSetAsCurrent,
}: AgendaAdminCardProps) {
  const { selectedItem, closeMenu, openMenu } = useListMenu<AgendaItemDoc>();
  const [items, setItems] = useState<AgendaItemDoc[]>(agendaItems);
  const agendaItemsWithStartTime = useAgendaItemsWithStartTime(
    items,
    event.startDate,
    track.startTimeDelta,
    track.mode,
    track.currentAgendaItemId,
  );

  const { t } = useTranslation();

  const DragOverlayRenderer = useCallback(
    ({ item, sx }: { item: AgendaItemWithTimes; sx?: SxProps<Theme> }) => (
      <AgendaAdminCardListItem
        agendaItem={item}
        sx={sx}
        onMenuClick={openMenu}
        showMenu={Boolean(onEdit || onSetAsCurrent || onDelete)}
      />
    ),
    [onDelete, onEdit, onSetAsCurrent, openMenu],
  );

  const handleOnUpdateOrder = useCallback(
    (items: AgendaItemDoc[]) => {
      setItems(items);
      onUpdateOrder && onUpdateOrder(items);
    },
    [onUpdateOrder],
  );

  useEffect(() => {
    // Update items when agendaItems prop changes
    setItems(agendaItems);
  }, [agendaItems]);

  return (
    <Card
      sx={{
        position: 'relative',
        maxHeight: '100dvh',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      aria-labelledby="agenda-card-header"
    >
      <CardHeader title={t('Agenda')} id="agenda-card-header" />
      <CardContent sx={{ height: '100%', overflow: 'auto', p: 0, m: 2 }}>
        <SortableList
          initialItems={agendaItemsWithStartTime}
          DragOverlayRenderer={DragOverlayRenderer}
          getLabel={(item) => item.title}
          onUpdateItems={handleOnUpdateOrder}
        >
          <List>
            {agendaItemsWithStartTime.map((agendaItem) => (
              <SortableAgendaListItem
                key={agendaItem.id}
                agendaItem={agendaItem}
                onMenuClick={openMenu}
                showMenu={Boolean(onEdit || onDelete)}
                onClick={() => onSelect && onSelect(agendaItem)}
              />
            ))}
          </List>
        </SortableList>
      </CardContent>
      <CardActions>
        {onOpenAddAgendaItemDialog ? (
          <Button size="small" onClick={() => onOpenAddAgendaItemDialog()}>
            {t('Add new agenda item')}
          </Button>
        ) : null}
      </CardActions>
      <ListItemMenu
        selectedItem={selectedItem}
        onClose={closeMenu}
        onDelete={onDelete}
        onEdit={onEdit}
        onSetAsCurrent={onSetAsCurrent}
      />
    </Card>
  );
}

function SortableAgendaListItem({ agendaItem, ...props }: AgendaListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: agendaItem.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
  };
  return (
    <AgendaAdminCardListItem
      agendaItem={agendaItem}
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
}

export default AgendaAdminCard;
